import { useEffect, useState, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import Swal from "sweetalert2";
import { useDarkMode } from "../../../../../context/ThemeContext";
import { useAlert } from "../../../../../context/AlertContext";
import { useTrackingLinksStore } from "../../../../../store/admin/trackingLinksStore";
import { useTrackingLinksList } from "../../../../../features/trackingLinks/queries";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { qk } from "../../../../../lib/queryKeys";
import { useCompaniesStore } from "../../../../../store/admin/companiesStore";
import { allUnitsQueryOptions } from "../../../../../features/units/queries";
import type { ThemeClasses } from "./types";
import type { TrackingLink } from "../../../../../store/admin/trackingLinksStore";
import { TrackingLinkHeader } from "./components/TrackingLinkHeader";
import { TrackingLinkList } from "./components/TrackingLinkList";
import { TrackingLinkPagination } from "./components/TrackingLinkPagination";
import { TrackingLinkModal, type TrackingLinkFormData } from "./components";
import { TrackingLinkDetailsModal } from "./components/TrackingLinkDetailsModal";
import { escapeHtml } from "../../../../../utils/escapeHtml";

const scrollbarStyles = `
  .scrollbar-light::-webkit-scrollbar {
    width: 6px;
  }
  .scrollbar-light::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  .scrollbar-light::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  .scrollbar-light::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  
  .scrollbar-dark::-webkit-scrollbar {
    width: 6px;
  }
  .scrollbar-dark::-webkit-scrollbar-track {
    background: #374151;
    border-radius: 3px;
  }
  .scrollbar-dark::-webkit-scrollbar-thumb {
    background: #6b7280;
    border-radius: 3px;
  }
  .scrollbar-dark::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

export default function TrackingLinksSection() {
  const { isDarkMode } = useDarkMode();
  const { showAlert } = useAlert();
  const {
    createTrackingLink,
    updateTrackingLink,
    deleteTrackingLink,
    extendTrackingLink,
    getTrackingLinkDetails,
  } = useTrackingLinksStore();
  const companyId = useCompaniesStore((state) => state.companyId);
  const queryClient = useQueryClient();

  // Cleanup scroll lock on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Estados de filtros
  const [accessType, setAccessType] = useState("all");
  const [status, setStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedView, setSelectedView] = useState<"grid" | "list">("list");
  const itemsPerPage = window.innerWidth >= 1536 ? 8 : 6; // 2xl+ = 8 items, lg = 6 items
  const debouncedSearch = useDebounce(searchQuery);

  const filterParams = useMemo(
    () => ({
      page: currentPage,
      limit: itemsPerPage,
      accessType: accessType !== "all" ? accessType : undefined,
      status: status !== "all" ? status : undefined,
      search: debouncedSearch || undefined,
    }),
    [currentPage, itemsPerPage, accessType, status, debouncedSearch],
  );

  const trackingLinksQuery = useTrackingLinksList(filterParams);
  const trackingLinks = trackingLinksQuery.data?.data ?? [];
  const meta = trackingLinksQuery.data?.meta;
  const loading = trackingLinksQuery.isLoading;

  const invalidateTrackingLinks = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["trackingLinks"] });
    queryClient.invalidateQueries({ queryKey: qk.counts(companyId) });
  }, [queryClient, companyId]);

  // Estados del modal
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editTrackingLink, setEditTrackingLink] = useState<TrackingLink | null>(
    null,
  );
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  // Estado para el modal de detalles
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsTrackingLink, setDetailsTrackingLink] = useState<TrackingLink | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Unidades desde la cache compartida (qk.vehicles.all); solo al abrir el modal
  const unitsQuery = useQuery({
    ...allUnitsQueryOptions(companyId),
    enabled: !!companyId && showModal,
  });
  const units = unitsQuery.data ?? [];

  // Form hook
  const form = useForm<TrackingLinkFormData>({
    mode: "onChange",
    defaultValues: {
      name: "",
      internalNotes: "",
      units: [],
      unitSelection: "none",
      accessType: "public",
      accessCode: "",
      activationType: "immediate",
      activationDate: "",
      durationValue: 24,
      durationUnit: "hours",
    },
  });

  // Theme classes
  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  // Handlers de filtros
  const handleAccessTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAccessType(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatus(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  // Handlers de vista
  const handleViewChange = (view: "grid" | "list") => {
    setSelectedView(view);
  };

  // Handlers de modal
  const handleAddNew = () => {
    form.reset({
      name: "",
      internalNotes: "",
      units: [],
      unitSelection: "none",
      accessType: "public",
      accessCode: "",
      activationType: "immediate",
      activationDate: "",
      durationValue: 24,
      durationUnit: "hours",
    });
    setIsEdit(false);
    setEditTrackingLink(null);
    setShowModal(true);
    // Disable background scroll
    document.body.style.overflow = "hidden";
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEdit(false);
    setEditTrackingLink(null);
    setLoadingSubmit(false);
    form.reset();
    // Enable background scroll
    document.body.style.overflow = "";
  };

  const handleEdit = useCallback(
    (trackingLink: TrackingLink) => {
      form.reset({
        name: trackingLink.name,
        internalNotes: trackingLink.internalNotes || "",
        units: trackingLink.units || [],
        unitSelection: trackingLink.units?.length > 0 ? "specific" : "none",
        accessType: trackingLink.accessType,
        accessCode: trackingLink.hasAccessCode ? "••••••••" : "",
        activationType: trackingLink.activationType,
        activationDate: trackingLink.activationDate || "",
        durationValue: trackingLink.durationValue,
        durationUnit: trackingLink.durationUnit,
      });
      setEditTrackingLink(trackingLink);
      setIsEdit(true);
      setShowModal(true);
      // Disable background scroll
      document.body.style.overflow = "hidden";
    },
    [form],
  );

  const handleActivate = useCallback(
    async (trackingLink: TrackingLink) => {
      const result = await Swal.fire({
        title: "¿Activar link?",
        text: `¿Estás seguro de activar el link "${trackingLink.name}"? Esta acción lo hará disponible para su uso.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#10b981",
        cancelButtonColor: "#6b7280",
        confirmButtonText: "Sí, activar",
        cancelButtonText: "Cancelar",
      });

      if (!result.isConfirmed) return;

      const success = await updateTrackingLink(trackingLink.id, {
        status_id: "R5tW0uX4yY9cF6dH2aI8",
      });

      if (success) {
        showAlert({
          title: "Éxito",
          text: "Link activado correctamente",
          icon: "success",
        });
        // Refetch to update list
        invalidateTrackingLinks();
      } else {
        showAlert({
          title: "Error",
          text: "Error al activar el link",
          icon: "error",
        });
      }
    },
    [
      updateTrackingLink,
      showAlert,
      invalidateTrackingLinks,
      currentPage,
      itemsPerPage,
      accessType,
      status,
      searchQuery,
    ],
  );

  const handleDelete = useCallback(
    async (trackingLink: TrackingLink) => {
      const result = await Swal.fire({
        title: "¿Eliminar link?",
        text: `¿Estás seguro de eliminar el link "${trackingLink.name}"? Esta acción lo desactivará.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });

      if (!result.isConfirmed) return;

      const success = await deleteTrackingLink(trackingLink.id);
      if (success) {
        showAlert({
          title: "Éxito",
          text: "Link desactivado correctamente",
          icon: "success",
        });
        // Refetch to update list
        invalidateTrackingLinks();
      } else {
        showAlert({
          title: "Error",
          text: "Error al desactivar el link",
          icon: "error",
        });
      }
    },
    [
      deleteTrackingLink,
      showAlert,
      invalidateTrackingLinks,
      currentPage,
      itemsPerPage,
      accessType,
      status,
      searchQuery,
    ],
  );

  const handleExtend = useCallback(
    async (trackingLink: TrackingLink) => {
      const result = await Swal.fire({
        title: "Renovar Link",
        html: `
          <p>Extender la vigencia del link "<strong>${escapeHtml(String(trackingLink.name ?? ""))}</strong>"</p>
          <div style="margin: 20px 0;">
            <label for="durationValue" style="display: block; margin-bottom: 5px;">Duración:</label>
            <input id="durationValue" type="number" min="1" max="365" value="24" style="width: 80px; padding: 5px; margin-right: 10px;">
            <select id="durationUnit" style="padding: 5px;">
              <option value="hours">Horas</option>
              <option value="days" selected>Días</option>
              <option value="weeks">Semanas</option>
              <option value="months">Meses</option>
            </select>
          </div>
        `,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#10b981",
        cancelButtonColor: "#6b7280",
        confirmButtonText: "Renovar",
        cancelButtonText: "Cancelar",
        preConfirm: () => {
          const durationValue = (Swal.getPopup() as any).querySelector('#durationValue').value;
          const durationUnit = (Swal.getPopup() as any).querySelector('#durationUnit').value;
          
          if (!durationValue || durationValue < 1 || durationValue > 365) {
            Swal.showValidationMessage('La duración debe estar entre 1 y 365');
            return false;
          }
          
          return { durationValue: parseInt(durationValue), durationUnit };
        }
      });

      if (!result.isConfirmed || !result.value) return;

      try {
        const success = await extendTrackingLink(
          trackingLink.id,
          result.value.durationValue,
          result.value.durationUnit
        );

        if (success) {
          showAlert({
            title: "Éxito",
            text: "Link renovado correctamente",
            icon: "success",
          });
          // Refetch to update list
          invalidateTrackingLinks();
        } else {
          showAlert({
            title: "Error",
            text: "Error al renovar el link",
            icon: "error",
          });
        }
      } catch (error) {
        console.error("Error extending link:", error);
        showAlert({
          title: "Error",
          text: "Error al procesar la solicitud",
          icon: "error",
        });
      }
    },
    [
      extendTrackingLink,
      showAlert,
      invalidateTrackingLinks,
      currentPage,
      itemsPerPage,
      accessType,
      status,
      searchQuery,
    ],
  );

  const handleDetails = useCallback(
    async (trackingLink: TrackingLink) => {
      setLoadingDetails(true);
      try {
        const details = await getTrackingLinkDetails(trackingLink.id);
        setDetailsTrackingLink(details);
        setShowDetailsModal(true);
      } catch (error) {
        console.error("Error fetching tracking link details:", error);
        showAlert({
          title: "Error",
          text: "No se pudieron cargar los detalles del link",
          icon: "error",
        });
      } finally {
        setLoadingDetails(false);
      }
    },
    [getTrackingLinkDetails, showAlert],
  );

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setDetailsTrackingLink(null);
  };

  const handleSubmit = async (data: TrackingLinkFormData) => {
    setLoadingSubmit(true);

    try {
      if (isEdit && editTrackingLink) {
        const result = await updateTrackingLink(editTrackingLink.id, {
          name: data.name,
          internalNotes: data.internalNotes,
          units:
            data.unitSelection === "all"
              ? units.map((unit) => unit.id)
              : data.unitSelection === "none"
                ? []
                : data.units,
          accessType: data.accessType,
          accessCode:
            data.accessType === "protected" ? data.accessCode : undefined,
          durationValue: data.durationValue,
          durationUnit: data.durationUnit,
        });

        if (result) {
          showAlert({
            title: "Éxito",
            text: "Link actualizado correctamente",
            icon: "success",
          });
          handleCloseModal();
          // Refetch to update list
          invalidateTrackingLinks();
        } else {
          showAlert({
            title: "Error",
            text: "Error al actualizar el link",
            icon: "error",
          });
        }
      } else {
        if (!companyId) {
          showAlert({
            title: "Error",
            text: "No se ha seleccionado una compañía",
            icon: "error",
          });
          setLoadingSubmit(false);
          return;
        }

        const result = await createTrackingLink({
          name: data.name,
          internalNotes: data.internalNotes,
          units:
            data.unitSelection === "all"
              ? units.map((unit) => unit.id)
              : data.unitSelection === "none"
                ? []
                : data.units,
          accessType: data.accessType,
          accessCode:
            data.accessType === "protected" ? data.accessCode : undefined,
          activationType: data.activationType,
          activationDate:
            data.activationType === "scheduled"
              ? data.activationDate
              : undefined,
          durationValue: data.durationValue,
          durationUnit: data.durationUnit,
          company_id: companyId,
        });

        if (result) {
          showAlert({
            title: "Éxito",
            text: "Link creado correctamente",
            icon: "success",
          });
          handleCloseModal();
          // Refetch to update list
          invalidateTrackingLinks();
        } else {
          showAlert({
            title: "Error",
            text: "Error al crear el link",
            icon: "error",
          });
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showAlert({
        title: "Error",
        text: "Error al procesar la solicitud",
        icon: "error",
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleCopy = useCallback(
    (trackingLink: TrackingLink) => {
      const fullUrl = `${window.location.origin}${trackingLink.publicUrl}`;
      navigator.clipboard.writeText(fullUrl);
      showAlert({
        title: "Éxito",
        text: "Link copiado al portapapeles",
        icon: "success",
      });
    },
    [showAlert],
  );

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div className="h-full flex flex-col">
        {/* Header */}
        <TrackingLinkHeader
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          accessType={accessType}
          status={status}
          searchQuery={searchQuery}
          selectedView={selectedView}
          onAccessTypeChange={handleAccessTypeChange}
          onStatusChange={handleStatusChange}
          onSearchChange={handleSearchChange}
          onViewChange={handleViewChange}
          onAddNew={handleAddNew}
        />

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6">
          <TrackingLinkList
            trackingLinks={trackingLinks}
            loading={loading}
            selectedView={selectedView}
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onCopy={handleCopy}
            onActivate={handleActivate}
            onExtend={handleExtend}
            onDetails={handleDetails}
            loadingDetails={loadingDetails}
          />

          {/* Paginación */}
          <TrackingLinkPagination
            currentPage={currentPage}
            totalPages={meta?.totalPages || 0}
            onPageChange={setCurrentPage}
          />
        </div>

        {/* Modal de Creación/Edición */}
        <TrackingLinkModal
          isOpen={showModal}
          isEdit={isEdit}
          loadingSubmit={loadingSubmit}
          form={form}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          units={units}
          onSubmit={handleSubmit}
          onClose={handleCloseModal}
        />

        {/* Modal de Detalles */}
        <TrackingLinkDetailsModal
          isOpen={showDetailsModal}
          onClose={handleCloseDetailsModal}
          trackingLink={detailsTrackingLink}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
        />
      </div>
    </>
  );
}
